import {
	FREE_PLAN_DESCRIPTIONS,
	PLUS_PLAN_DESCRIPTIONS,
	BUSINESS_PLAN_DESCRIPTIONS,
	ENTERPRISE_PLAN_DESCRIPTIONS,
	ENGAGE_PLAN_DESCRIPTIONS,
	SCALE_PLAN_DESCRIPTIONS,
	FREE_CUSTOMER_PLAN_DESCRIPTIONS,
	ENTERPRISE_CUSTOMER_PLAN_DESCRIPTIONS,
} from '@/components/Billing/billing-service-plan-descriptions.const';
import { PlanGroups, PlanTypes, ProductType, BillingLevel } from '@/components/Billing/billing-types';
import { AccountRoles } from '@/utils/account-service.const';
export const ANNUAL_PLANS_DISCOUNT = 20;

export const BILLING_LEVEL_TO_RECOMMENDED_ROLE: Record<BillingLevel, AccountRoles> = {
	[BillingLevel.LiteMember]: AccountRoles.LiteEndUser,
	[BillingLevel.StandardMember]: AccountRoles.StandardEndUser,
	[BillingLevel.PremiumMember]: AccountRoles.PremiumEndUser,
};
const COMPARE_MEMBER_TYPES_LINK = `https://docs.ply.io/manage-and-collaborate/pricing-guide/workspace-member-types`;
const SEE_SERVICE_AND_CREDIT_COST_LINK = `https://docs.ply.io/manage-and-collaborate/pricing-guide/ply-services-and-credits`;
const WORKSPACE_MEMBER_MANAGEMENT_LINK = `https://app.ply.io/settings/organization/users`;
export const LEARN_MORE_OVERAGES_LINK = 'https://docs.ply.io/manage-and-collaborate/pricing-guide/exceeding-quotas';
export const BILLING_FAQ_URL = `https://docs.ply.io/manage-and-collaborate/pricing-guide/billing-faq`;
export const HOW_IT_WORKS_EXPLANATIONS = {
	[ProductType.TeamFeatures]: [
		`<span>Both creators and end-users need to be members - <a class="!text-[#646e87]" href="${COMPARE_MEMBER_TYPES_LINK}" target="_blank">compare member types.</a></span>`,
		`<span>Members and types are counted automatically based on your <a class="!text-[#646e87]" href="${WORKSPACE_MEMBER_MANAGEMENT_LINK}" target="_blank">workspace member management.</a></span>`,
		`Every paid member contributes team feature tasks to the shared pool used by all members.`,
		`Team feature tasks are used for every step completed successfully in team features.`,
	],
	[ProductType.CustomerFeatures]: [
		`Get a task plan. Quota resets monthly, whether paid monthly or annually.`,
		`Customer feature tasks are used for every step completed successfully in portals.`,
	],
	[ProductType.Automation]: [
		`Get a task plan. Quota resets monthly, whether paid monthly or annually.`,
		`Automation tasks are used for every step completed successfully in an automation feature.`,
	],
	[ProductType.Credits]: [
		`Get a credits plan. Quota resets monthly, whether paid monthly or annually.`,
		`Credits are used for every credit-based step completed successfully in any feature.`,
		`<a class="!text-[#646e87]" href="${SEE_SERVICE_AND_CREDIT_COST_LINK}" target="_blank">See services and credit cost list</a>`,
	],
};
export const LEARN_MORE_BASE_LINK = 'https://docs.ply.io/manage-and-collaborate/pricing-guide';
export const LEARN_MORE_LINK = {
	[ProductType.TeamFeatures]: `${LEARN_MORE_BASE_LINK}#team-features`,
	[ProductType.CustomerFeatures]: `${LEARN_MORE_BASE_LINK}#customer-features`,
	[ProductType.Automation]: `${LEARN_MORE_BASE_LINK}#automations-add-on`,
	[ProductType.Credits]: `${LEARN_MORE_BASE_LINK}#ply-credits-add-on`,
};
export const BILLING_LEVEL_HIERARCHY: BillingLevel[] = [BillingLevel.LiteMember, BillingLevel.StandardMember, BillingLevel.PremiumMember];

export enum PlanIntervals {
	Monthly = 'month',
	Annually = 'year',
}
export const DOWNGRADE_UNIT_AMOUNT = 'UnitAmount';
export const DOWNGRADE_SCHEDULE_TYPE = 'Downgrade';

export const VIRTUALLY_UNLIMITED_NUM_OF_TASKS_PER_USER = 10000;

export const BOOK_A_CALL_LINK = 'https://portals.ply.so/talk-to-sales/';
export const ROLES_AND_PERMISSIONS_LINK = 'https://docs.ply.io/manage-and-collaborate/roles-and-permissions';
export const MEMBER_MANAGEMENT_LINK = `${process.env.VUE_APP_APP_URL}settings/organization/users`;

export const CREDITS_VIDEO_URL = '';
export const AUTOMATION_VIDEO_URL = '';
export const TEAM_FEATURES_VIDEO_URL = '';
export const CUSTOMER_FEATURES_VIDEO_URL = '';

export const DATE_FORMAT_DISPLAY = 'MMM DD, YYYY';

export const SECTION_DESCRIPTION = {
	[ProductType.TeamFeatures]: `Streamline processes with add-ons in your team's favorite apps and command bar tools accessible on any website.`,
	[ProductType.CustomerFeatures]: `Elevate customer experiences with portals - they’re like forms with superpowers.`,
	[ProductType.Automation]: `Build features that run automatically, without a user.`,
	[ProductType.Credits]: `Use credits to run Ply's prebuilt Al, OCR, translation and more.`,
};

export const PLAN_DESCRIPTIONS_TITLES: Record<PlanTypes, Record<PlanGroups, { text: string; isSupported: boolean }[]>> = {
	[PlanTypes.TeamFeatures]: {
		[PlanGroups.Beta]: 'Everything you need to get started:',
		[PlanGroups.Free]: 'Everything you need to get started:',
		[PlanGroups.Plus]: 'Everything in Free, and:',
		[PlanGroups.Business]: 'Everything in Plus, and:',
		[PlanGroups.Enterprise]: 'Everything in Business, and:',
	},
	[PlanTypes.CustomerFeatures]: {
		[PlanGroups.Beta]: 'Everything you need to get started:',
		[PlanGroups.Free]: 'Everything you need to get started:',
		[PlanGroups.Engage]: 'Everything in Starter, and:',
		[PlanGroups.Scale]: 'Everything in Engage, and:',
		[PlanGroups.Enterprise]: 'Everything in Scale, and:',
	},
};
export const PLAN_DESCRIPTIONS: Record<PlanTypes, Record<PlanGroups, { text: string; isSupported: boolean }[]>> = {
	[PlanTypes.TeamFeatures]: {
		[PlanGroups.Beta]: FREE_PLAN_DESCRIPTIONS,
		[PlanGroups.Free]: FREE_PLAN_DESCRIPTIONS,
		[PlanGroups.Plus]: PLUS_PLAN_DESCRIPTIONS,
		[PlanGroups.Business]: BUSINESS_PLAN_DESCRIPTIONS,
		[PlanGroups.Enterprise]: ENTERPRISE_PLAN_DESCRIPTIONS,
	},
	[PlanTypes.CustomerFeatures]: {
		[PlanGroups.Beta]: FREE_CUSTOMER_PLAN_DESCRIPTIONS,
		[PlanGroups.Free]: FREE_CUSTOMER_PLAN_DESCRIPTIONS,
		[PlanGroups.Scale]: SCALE_PLAN_DESCRIPTIONS,
		[PlanGroups.Engage]: ENGAGE_PLAN_DESCRIPTIONS,
		[PlanGroups.Enterprise]: ENTERPRISE_CUSTOMER_PLAN_DESCRIPTIONS,
	},
	[PlanTypes.Automation]: {
		[PlanGroups.Free]: FREE_PLAN_DESCRIPTIONS,
		[PlanGroups.Enterprise]: ENTERPRISE_PLAN_DESCRIPTIONS,
	},
	[PlanTypes.Credits]: {
		[PlanGroups.Free]: FREE_PLAN_DESCRIPTIONS,
		[PlanGroups.Enterprise]: ENTERPRISE_PLAN_DESCRIPTIONS,
	},
};
export const VIRTUALLY_UNLIMITED_IDENTIFIER = 'unlimited';
export const COMPARE_BILLING_PLANS_URL = 'https://ply.io/pricing';
export const BILLING_MANAGE_PLANS_HREF = `${process.env.VUE_APP_APP_URL}billing`;
export const BILLING_TERMS_AND_CONDITION_HREF = 'https://info.ply.io/terms-of-service';
